import { API_BASE_URL, ApiError, fetchWithTimeout } from "./shared";
import type { StrategyPricingTier } from "./strategy-pricing";

// ============================================================================
// Billing / Invoices API (SFWL-015 — monthly upfront invoicing)
// ============================================================================

/**
 * Entire `v1/billing` surface is `AuthGuard` + `WriteAccessGuard` (via
 * `@RequiresWriteAccess()`) server-side, deliberately including the GETs
 * here — view-only members 403 on every route, not just mutations (the
 * locked AC is "view-only members cannot access billing"). Tenant scoping
 * is the usual `x-tenant-id` header attached globally by `fetchWithTimeout`.
 * All money fields are decimal strings end-to-end — same `formatMoneyFromString`
 * rule as strategy pricing, never `parseFloat` for math.
 */

export type InvoiceStatus = "issued" | "paid";

/** Whose invoice this is relative to the viewing tenant — a parent viewer
 *  sees its own invoices plus each child's, labeled `child`. */
export type InvoiceRelationship = "own" | "child";

/** `GET v1/billing/invoices` list item — no line items (see `InvoiceDetailDto`). */
export interface InvoiceListItemDto {
  id: string;
  tenantId: string;
  /**
   * Human-readable label for the billed tenant — a live `Tenant.name`
   * read (falls back server-side to the invoice's own `billTo.companyName`
   * snapshot if the tenant row is gone). Deliberately NOT part of the
   * AC8 snapshot freeze: money/tier/promo/billTo/issuer/line items stay
   * byte-identical across re-reads, but this label can drift if the
   * tenant is renamed after the invoice issued.
   */
  tenantName: string;
  relationship: InvoiceRelationship;
  invoiceNumber: string;
  periodYear: number;
  periodMonth: number;
  status: InvoiceStatus;
  issuedAt: string;
  paidAt: string | null;
  emailedAt: string | null;
  /**
   * Review-fleet P3 fix: `emailError` (raw resend/provider error text) is
   * deliberately NOT exposed on this tenant-facing DTO — `invoices.email_error`
   * still exists and is still logged server-side, this is a DTO-exposure fix
   * only (see `api/src/modules/billing/dto/invoice.dto.ts`). `emailedAt`
   * alone is enough for a tenant to see "was this invoice emailed" without
   * leaking provider-internal failure text (auth errors, rate-limit
   * messages, etc.) to the tenant.
   */
  currency: string;
  subtotal: string;
  total: string;
}

export interface InvoiceLineItemDto {
  id: string;
  strategyId: string | null;
  strategyName: string;
  tier: StrategyPricingTier;
  unitPriceMonthly: string;
  promoCode: string | null;
  promoDiscountPct: string | null;
  effectiveUnitPrice: string;
  quantity: number;
  amount: string;
  pricingId: string | null;
  sortOrder: number;
}

/** Snapshot captured at invoice generation — never re-derived from live tenant data. */
export interface InvoiceBillTo {
  companyName: string | null;
  contactEmail: string | null;
  address: string | null;
}

/** The parent tenant `{name, logoUrl}` for a child invoice, or the platform identity otherwise. */
export interface InvoiceIssuer {
  name: string;
  logoUrl: string | null;
}

/** `GET v1/billing/invoices/:id` — adds billTo/issuer/lineItems. */
export interface InvoiceDetailDto extends InvoiceListItemDto {
  pricingAsOf: string;
  billTo: InvoiceBillTo;
  issuer: InvoiceIssuer;
  lineItems: InvoiceLineItemDto[];
}

async function handleBillingResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `API error: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Response body is not JSON (or absent), use default message
    }
    throw new ApiError(errorMessage, response.status);
  }
  return response.json();
}

const BILLING_BASE = `${API_BASE_URL}/api/v1/billing`;

export const invoicesApi = {
  /** Active tenant's invoices, plus children's (labeled `relationship: 'child'`) for a parent viewer. */
  async list(): Promise<InvoiceListItemDto[]> {
    const response = await fetchWithTimeout(`${BILLING_BASE}/invoices`);
    return handleBillingResponse<InvoiceListItemDto[]>(response);
  },

  /** One invoice with billTo/issuer/lineItems. */
  async get(id: string): Promise<InvoiceDetailDto> {
    const response = await fetchWithTimeout(`${BILLING_BASE}/invoices/${id}`);
    return handleBillingResponse<InvoiceDetailDto>(response);
  },

  /**
   * Streams the rendered PDF as a blob. Deliberately routed through
   * `fetchWithTimeout` (not a bare `<a href>`) so the globally-attached
   * `x-tenant-id` header rides along — a direct browser navigation to the
   * URL would drop it and 400 on the missing tenant context.
   */
  async getPdfBlob(id: string): Promise<Blob> {
    const response = await fetchWithTimeout(`${BILLING_BASE}/invoices/${id}/pdf`);
    if (!response.ok) {
      // Error responses are JSON even though the happy path streams
      // application/pdf — reuse the same JSON-message parsing.
      await handleBillingResponse<never>(response);
    }
    return response.blob();
  },
};
